import { HomeFooter } from "@/components/boardblend/home-footer";
import { LegalHeader } from "@/components/boardblend/legal-header";
import { PublicPageHead } from "@/components/seo/public-page-head";
import { staticPublicPages } from "@/lib/public-seo";

export default function PrivacyPage() {
  return (
    <>
      <PublicPageHead page={staticPublicPages.privacy} />
      <main className="legal-page">
        <LegalHeader />
        <article>
          <p className="eyebrow">Privacy Policy</p>
          <h1>Privacy Policy</h1>
          <p>
            <strong>Effective date: July 23, 2026</strong>
          </p>
          <p>
            This Privacy Policy explains what information Boardblend collects,
            how we use it, and the choices available to you when you use
            Boardblend.
          </p>

          <h2>1. Information we collect</h2>
          <h3>Account information</h3>
          <p>When you create an account, we may collect:</p>
          <ul>
            <li>Name and email address</li>
            <li>Profile information</li>
            <li>Login and authentication details</li>
            <li>Subscription status</li>
          </ul>

          <h3>Boardblend content</h3>
          <p>
            We store the content you create, upload, or share through
            Boardblend, including:
          </p>
          <ul>
            <li>Canvases and session history</li>
            <li>Comments and contributions</li>
            <li>Uploaded documents and images</li>
            <li>Transcripts</li>
            <li>Session titles and settings</li>
            <li>Information submitted by connected AI assistants</li>
          </ul>
          <p>
            This content may include personal information about you or other
            people.
          </p>

          <h3>Transcription data</h3>
          <p>
            When a session leader enables transcription, Boardblend processes
            audio from their microphone or a selected browser tab to create an
            approximate live transcript.
          </p>
          <p>
            The transcript becomes part of the shared brainstorm. Boardblend
            does not retain the original audio after it has been processed.
          </p>
          <p>
            Session leaders are responsible for informing participants and
            obtaining any consent required before recording or transcribing a
            conversation.
          </p>

          <h3>Connected AI services</h3>
          <p>
            You may connect a compatible third-party AI service to Boardblend.
            When you do, shared session content—such as the transcript, canvas,
            comments, and uploaded documents—may be sent to that provider at
            your direction.
          </p>
          <p>
            Boardblend does not receive or store your private conversations with
            a connected AI service unless you deliberately submit information
            from that conversation to a Boardblend session. The AI provider
            processes information under its own terms and privacy policy.
          </p>

          <h3>Payment information</h3>
          <p>
            Payments are processed by Stripe. We receive limited billing
            information, such as your subscription status, transaction details,
            and the last four digits of your payment method. We do not store
            complete payment-card details.
          </p>

          <h3>Usage and technical information</h3>
          <p>We may automatically collect:</p>
          <ul>
            <li>IP address</li>
            <li>Browser, device, and operating system information</li>
            <li>Pages and features used</li>
            <li>Dates and times of access</li>
            <li>Error reports and diagnostic data</li>
            <li>Referring pages</li>
            <li>Cookie and similar identifier data</li>
          </ul>

          <h2>2. How we use information</h2>
          <p>We use information to:</p>
          <ul>
            <li>Provide and maintain Boardblend</li>
            <li>Create and synchronize collaborative sessions</li>
            <li>Generate transcripts</li>
            <li>Process subscriptions and payments</li>
            <li>Provide support</li>
            <li>Improve performance and usability</li>
            <li>Detect fraud, abuse, and security threats</li>
            <li>Communicate important service updates</li>
            <li>Comply with legal obligations</li>
          </ul>
          <p>We do not use brainstorm content to train our own AI models.</p>

          <h2>3. How session content is shared</h2>
          <p>Boardblend is a collaborative service.</p>
          <p>Content added to a session may be visible to:</p>
          <ul>
            <li>The session owner</li>
            <li>People invited to the session</li>
            <li>Participants who join using its room code</li>
            <li>Connected AI services authorized by participants</li>
          </ul>
          <p>
            Private AI conversations remain private unless the participant
            chooses to share their contents.
          </p>
          <p>
            The session owner controls who can join and which proposed changes
            become part of the shared canvas. Participants should avoid
            uploading information they are not authorized to share.
          </p>

          <h2>4. How we disclose information</h2>
          <h3>Service providers</h3>
          <p>
            We may disclose information to companies that help us provide
            hosting, authentication, analytics, transcription, email, customer
            support, security, and payment services. They may process
            information only to provide services to us and under appropriate
            contractual restrictions.
          </p>

          <h3>Connected AI providers</h3>
          <p>
            When you choose to connect an AI service, we share the session
            information required to support that connection.
          </p>

          <h3>Other session participants</h3>
          <p>
            We share session content according to the collaboration features
            you use and the permissions established by the session owner.
          </p>

          <h3>Legal and safety purposes</h3>
          <p>We may disclose information when reasonably necessary to:</p>
          <ul>
            <li>Comply with law or a valid legal request</li>
            <li>Protect the security of Boardblend</li>
            <li>Prevent fraud or abuse</li>
            <li>Protect the rights and safety of users or others</li>
          </ul>

          <h3>Business transactions</h3>
          <p>
            Information may be transferred as part of a merger, financing,
            acquisition, reorganization, or sale of assets. We will take
            reasonable steps to ensure it remains protected.
          </p>

          <h2>5. Selling and advertising</h2>
          <p>We do not sell personal information.</p>
          <p>
            We do not share personal information for cross-context behavioural
            advertising and do not use brainstorm content to target
            advertisements.
          </p>

          <h2>6. Cookies and analytics</h2>
          <p>Boardblend may use cookies and similar technologies to:</p>
          <ul>
            <li>Keep you signed in</li>
            <li>Remember preferences</li>
            <li>Maintain security</li>
            <li>Understand how the service is used</li>
            <li>Diagnose errors and improve performance</li>
          </ul>
          <p>
            You can control cookies through your browser settings. Disabling
            essential cookies may prevent parts of Boardblend from working
            properly.
          </p>

          <h2>7. Retention and deletion</h2>
          <p>
            We retain information only for as long as reasonably necessary to
            provide Boardblend, meet legal obligations, resolve disputes, and
            maintain security.
          </p>
          <p>
            Session content is retained until the session owner deletes it or
            closes their account. Deleted content is removed from active
            systems and backups according to our standard deletion cycle,
            unless longer retention is legally required.
          </p>
          <p>
            Some transaction, security, and support records may be retained for
            longer where required for fraud prevention, accounting, or legal
            compliance.
          </p>

          <h2>8. Security</h2>
          <p>
            We use administrative, technical, and organizational safeguards
            designed to protect information against unauthorized access,
            disclosure, alteration, loss, or misuse.
          </p>
          <p>
            No online service can guarantee absolute security. You are
            responsible for protecting your account credentials and controlling
            who receives your room code.
          </p>

          <h2>9. International data transfers</h2>
          <p>
            Boardblend and its service providers may process information in
            countries outside your province, state, or country. Privacy laws in
            those locations may differ from those where you live. Where
            required, we use appropriate contractual and organizational
            safeguards for international transfers.
          </p>

          <h2>10. Your privacy rights</h2>
          <p>Depending on where you live, you may have the right to:</p>
          <ul>
            <li>Access personal information we hold about you</li>
            <li>Correct inaccurate information</li>
            <li>Request deletion of information</li>
            <li>Withdraw consent</li>
            <li>Object to or restrict certain processing</li>
            <li>Receive a portable copy of certain information</li>
            <li>Complain to a privacy regulator</li>
          </ul>
          <p>
            We may need to verify your identity before completing a request.
            Some information may be retained where permitted or required by
            law.
          </p>

          <h2>11. Business and team accounts</h2>
          <p>
            When an organization provides Boardblend to its employees or team
            members, that organization may control the account and session
            content.
          </p>
          <p>
            In those circumstances, Boardblend may process content on the
            organization&apos;s behalf. Privacy requests concerning
            organization-controlled content may need to be directed to that
            organization.
          </p>

          <h2>12. Children</h2>
          <p>
            Boardblend is not intended for children under 13, and we do not
            knowingly collect personal information from children under 13.
          </p>

          <h2>13. Changes to this policy</h2>
          <p>
            We may update this Privacy Policy as Boardblend changes. We will
            post the updated policy with a new effective date. Where changes
            materially affect your privacy rights, we will provide additional
            notice where appropriate.
          </p>
        </article>
        <HomeFooter />
      </main>
    </>
  );
}
